import { useEffect, useRef, useState } from 'react';
import isNull from 'lodash/isNull';

import { Nullable } from 'chaika-frontend-types/frontend-cms';
import { CountryCode } from 'chaika-enums';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { removeNotNumbers } from '@utils/removeNotNumbers';

import { PhoneCountryCode } from '../phoneCountry';
import { getMask, getPhoneCodeParams, getPhoneParams } from '../utils/phoneParams';

interface Params {
    value: string | number | readonly string[];
    country?: CountryCode;
    name?: string;
    onChangeValue?(value: string, name?: string): void;
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
    onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
    onCountryChange?(newValue: string): void;
}

interface Data {
    phone: string;
    countryCode: string;
    phoneMask: string;
    isOnFocus: boolean;
    setPhoneInput(element: HTMLElement): void;
    handlePhoneValueChange(value: string): void;
    handleCountrySelect(selectedCountryCode: string, mask?: string): void;
    handleFocus(event: React.FocusEvent<HTMLInputElement>): Promise<void>;
    handleBlur(event: React.FocusEvent<HTMLInputElement>): Promise<void>;
}

export function usePhoneInput({ value, country, name, onChangeValue, onBlur, onFocus, onCountryChange }: Params): Data {
    const focusState = useCheckOnFocus();
    const [currentValue, setCurrentValue] = useState<Nullable<string>>(null);
    const [countryCode, setCountryCode] = useState<string>(country ?? CountryCode.Ru);
    const [phoneMask, setPhoneMask] = useState<string>(getInitialMask(country));

    const phoneInputRef = useRef<HTMLElement | null>(null);
    const didMountRef = useRef(false);

    useEffect(() => {
        function applyCountryFromValue(rawValue: string): void {
            const { countryCode, phoneMask } = getPhoneParams(rawValue);

            if (!phoneMask) {
                return;
            }

            setCountryCode(countryCode);
            setPhoneMask(phoneMask);
        }

        if (!didMountRef.current) {
            if (value && value !== '') {
                applyCountryFromValue(value as string);
            }
        } else {
            applyCountryFromValue(value as string);
        }
    }, [value]);

    useEffect(() => {
        if (!didMountRef.current) {
            return;
        }

        setCountryCode(country ?? CountryCode.Ru);
        setPhoneMask(getInitialMask(country));
    }, [country]);

    useEffect(() => {
        didMountRef.current = true;
    }, []);

    function setPhoneInput(element: HTMLElement): void {
        phoneInputRef.current = element;
    }

    function handlePhoneValueChange(newValue: string): void {
        setCurrentValue(newValue);

        if (onChangeValue) {
            onChangeValue(newValue, name);
        }

        const { countryCode, phoneMask } = getPhoneCodeParams(newValue);

        if (!phoneMask) {
            return;
        }

        setCountryCode(countryCode);
        setPhoneMask(phoneMask);
    }

    function handleCountrySelect(selectedCountryCode: string, mask?: string): void {
        const newMask = mask ?? getMask(selectedCountryCode, currentValue ?? (value as string) ?? '');

        if (onCountryChange) {
            onCountryChange(removeNotNumbers(newMask));
        }

        setPhoneMask(newMask);
        setCountryCode(selectedCountryCode);

        phoneInputRef.current?.focus();
    }

    async function handleFocus(event: React.FocusEvent<HTMLInputElement>): Promise<void> {
        focusState.handleFocus();

        if (onFocus) {
            await onFocus(event);
        }
    }

    async function handleBlur(event: React.FocusEvent<HTMLInputElement>): Promise<void> {
        event.target.name = name ?? '';

        focusState.handleBlur();

        if (onBlur) {
            await onBlur(event);
        }

        setCurrentValue(null);
    }

    const phone = (isNull(currentValue) ? value : currentValue) as string;

    return {
        phone,
        countryCode,
        phoneMask,
        isOnFocus: focusState.isOnFocus,
        setPhoneInput,
        handlePhoneValueChange,
        handleCountrySelect,
        handleFocus,
        handleBlur,
    };
}

function getInitialMask(country?: CountryCode): string {
    return country
        ? getMask(country, PhoneCountryCode[country])
        : getMask(CountryCode.Ru, PhoneCountryCode[CountryCode.Ru]);
}
